// frontend/src/pages/manufacturing/v2/WOCreationLogV2.jsx
//
// Persistent audit trail of every "send to production" (v2 explosion)
// attempt — who sent what, when, and what came of it. Backed by
// GET /api/v2/explosion-log/.
//
// It exists because a line whose ordered qty is already fully covered by
// allocated stock produces NO work order, which used to read as a failure.
// Each row records an explicit outcome instead:
//   created          — work orders were made
//   covered          — nothing to make, stock already satisfies the line (good)
//   already_exploded — the line had been sent before
//   failed           — a genuine problem (e.g. a phantom product)
//
// Filters are all server-side; the free-text search is debounced (400ms) so
// typing doesn't hammer the endpoint — same pattern as TransactionHistoryV2.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Card, CardContent, Typography, Grid, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  LinearProgress, Alert, Link, Stack, Tooltip, InputAdornment,
} from '@mui/material';
import { Refresh, Search, FactCheck } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { v2 } from '../../../services/api';
import usePageTitle from '../../../hooks/usePageTitle';
import GenerationModeBadge from './components/GenerationModeBadge';

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 400;

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  { value: 'created', label: 'Created' },
  { value: 'covered', label: 'Covered by stock' },
  { value: 'already_exploded', label: 'Already sent' },
  { value: 'failed', label: 'Failed' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'line', label: 'Single line' },
  { value: 'order', label: 'Order' },
  { value: 'bulk', label: 'Bulk' },
  { value: 'remake', label: 'Remake' },
];

const OUTCOME_LABELS = {
  created: 'Created',
  covered: 'Covered by stock',
  already_exploded: 'Already sent',
  failed: 'Failed',
};

const OUTCOME_COLORS = {
  created: 'success',
  covered: 'info',
  already_exploded: 'default',
  failed: 'error',
};

const SOURCE_LABELS = {
  line: 'Single line',
  order: 'Order',
  bulk: 'Bulk',
  remake: 'Remake',
};

// An outcome the frontend doesn't know yet still renders readably rather than
// disappearing — new backend outcomes shouldn't blank a column.
const outcomeLabel = (o) => OUTCOME_LABELS[o] || o || '—';
const outcomeColor = (o) => OUTCOME_COLORS[o] || 'default';
const sourceLabel = (s) => SOURCE_LABELS[s] || s || '—';

function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Backend sends plain decimal strings ("40.0000"). Trim the noise but keep
// genuine fractions (up to 3 dp, matching the shop-floor display rule).
function formatQuantity(q) {
  if (q === null || q === undefined || q === '') return '—';
  const n = parseFloat(q);
  if (Number.isNaN(n)) return String(q);
  return String(Math.round(n * 1000) / 1000);
}

const MAX_REASON_CHARS = 60;

export default function WOCreationLogV2() {
  usePageTitle('WO Creation Log');
  const navigate = useNavigate();

  const [outcome, setOutcome] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadingRef = useRef(false);

  // Debounce the free-text filter.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Select/date filters reset to page 1 immediately.
  useEffect(() => { setPage(1); }, [outcome, source, dateFrom, dateTo]);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (outcome) p.outcome = outcome;
    if (source) p.source = source;
    if (debouncedSearch) p.search = debouncedSearch;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [page, outcome, source, debouncedSearch, dateFrom, dateTo]);

  const load = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res = await v2.explosionLog.list(params);
      const data = res.data || {};
      // DRF pagination shape: { count, results }. Fall back to a bare array.
      setRows(Array.isArray(data) ? data : (data.results || []));
      setCount(Array.isArray(data) ? data.length : (data.count ?? 0));
    } catch (err) {
      console.error('Failed to load WO creation log', err);
      setError(err);
      toast.error('Failed to load the WO creation log');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setOutcome(''); setSource(''); setSearch(''); setDateFrom(''); setDateTo('');
  };

  const hasActiveFilters = Boolean(outcome || source || search || dateFrom || dateTo);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const fromRow = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const toRow = Math.min(count, page * PAGE_SIZE);

  const handleRefresh = () => {
    load().then(() => toast.success('Refreshed')).catch(() => {});
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Card sx={{ mb: 3, border: '2px solid #1976d2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FactCheck color="primary" />
              WO Creation Log
              <GenerationModeBadge />
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Every attempt to send a customer-order line to production, newest
            first. &quot;Covered by stock&quot; means the line was already
            satisfied by allocated stock — no work order was needed.
          </Typography>
        </CardContent>
      </Card>

      {/* Filter bar */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="order no. / product / reason"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                  ),
                }}
                inputProps={{ 'data-testid': 'filter-search' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                label="Outcome"
                size="small"
                fullWidth
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                inputProps={{ 'data-testid': 'filter-outcome' }}
              >
                {OUTCOME_OPTIONS.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                label="Source"
                size="small"
                fullWidth
                value={source}
                onChange={(e) => setSource(e.target.value)}
                inputProps={{ 'data-testid': 'filter-source' }}
              >
                {SOURCE_OPTIONS.map((s) => (
                  <MenuItem key={s.value || 'all'} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date from"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                inputProps={{ 'data-testid': 'filter-date-from' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Date to"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                inputProps={{ 'data-testid': 'filter-date-to' }}
              />
            </Grid>
            {hasActiveFilters && (
              <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={clearFilters}
                  data-testid="clear-filters"
                >
                  Clear
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading && <LinearProgress data-testid="loading-state" />}
          {error && !loading && (
            <Alert severity="error" sx={{ my: 2 }} data-testid="error-state">
              Failed to load the WO creation log.
              <Button onClick={load} size="small" sx={{ ml: 1 }}>Retry</Button>
            </Alert>
          )}
          {!loading && !error && rows.length === 0 && (
            <Alert severity="info" sx={{ my: 2 }} data-testid="empty-state">
              <Typography variant="h6">No entries found</Typography>
              <Typography variant="body2">
                {hasActiveFilters
                  ? 'Try adjusting or clearing your filters.'
                  : 'Nothing has been sent to production yet.'}
              </Typography>
            </Alert>
          )}
          {!loading && !error && rows.length > 0 && (
            <>
              <TableContainer>
                <Table size="small" data-testid="wo-creation-log-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Line</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const reason = row.reason || '';
                      const truncated = reason.length > MAX_REASON_CHARS
                        ? `${reason.slice(0, MAX_REASON_CHARS)}…`
                        : reason;
                      return (
                        <TableRow key={row.id} hover data-testid={`wo-log-row-${row.id}`}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">{formatTimestamp(row.created_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.user_name || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            {row.order_number && row.order_id != null ? (
                              <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => navigate(`/manufacturing/customer-orders/${row.order_id}`)}
                                data-testid={`order-link-${row.id}`}
                              >
                                {row.order_number}
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {row.order_number || '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {row.line_id != null ? `#${row.line_id}` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {row.product_code || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.product_name || ''}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatQuantity(row.quantity)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={outcomeLabel(row.outcome)}
                              color={outcomeColor(row.outcome)}
                              data-testid={`outcome-chip-${row.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{sourceLabel(row.source)}</Typography>
                          </TableCell>
                          <TableCell>
                            {reason ? (
                              <Tooltip title={reason}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  data-testid={`reason-${row.id}`}
                                >
                                  {truncated}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Server-side pagination */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {fromRow}–{toRow} of {count}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="page-prev"
                >
                  Previous
                </Button>
                <Typography variant="body2" data-testid="page-indicator">
                  Page {page} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="page-next"
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
